import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, CheckCircle, Calendar, MapPin, Home, Hotel } from 'lucide-react';
import { PublicLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { getRSVP, getAccommodation, getPayment } from '@/lib/api';

const ConfirmationPage = () => {
  const { rsvpId } = useParams();
  const [loading, setLoading] = useState(true);
  const [rsvp, setRsvp] = useState(null);
  const [accommodation, setAccommodation] = useState(null);
  const [payment, setPayment] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const rsvpRes = await getRSVP(rsvpId);
        setRsvp(rsvpRes.data);
        
        try {
          const accRes = await getAccommodation(rsvpId);
          setAccommodation(accRes.data);
        } catch (e) {
          // No accommodation
        }
        
        try {
          const payRes = await getPayment(rsvpId);
          setPayment(payRes.data);
        } catch (e) {
          // No payment
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [rsvpId]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-stone">Chargement...</div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout showNav={false}>
      <section className="min-h-screen flex items-center justify-center py-16 px-6">
        <div className="max-w-lg w-full">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="w-20 h-20 bg-sage/10 rounded-full flex items-center justify-center mx-auto mb-8"
            >
              <CheckCircle className="w-10 h-10 text-sage" />
            </motion.div>

            {/* Title */}
            <h1 className="font-display text-3xl md:text-4xl text-charcoal mb-4" data-testid="confirmation-title">
              {rsvp?.attending ? 'Merci pour votre réponse !' : 'Réponse enregistrée'}
            </h1>

            {/* Message */}
            <p className="text-stone mb-8">
              {rsvp?.attending
                ? 'Nous avons hâte de vous retrouver le jour J !'
                : 'Merci de nous avoir répondu. Nous serons avec vous par la pensée.'}
            </p>

            {/* Summary Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card-elegant text-left mb-8"
            >
              <h3 className="font-display text-xl text-charcoal mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-terracotta" fill="#D4A373" />
                Récapitulatif
              </h3>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-stone-light">
                  <span className="text-stone">Famille</span>
                  <span className="font-medium text-charcoal">{rsvp?.family_name}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-stone-light">
                  <span className="text-stone">Présence</span>
                  <span className={`font-medium ${rsvp?.attending ? 'text-sage' : 'text-red-500'}`}>
                    {rsvp?.attending ? 'Confirmée' : 'Absent(s)'}
                  </span>
                </div>
                {rsvp?.attending && (
                  <>
                    <div className="flex justify-between py-2 border-b border-stone-light">
                      <span className="text-stone">Adultes</span>
                      <span className="font-medium text-charcoal">{rsvp?.adults?.length || 0}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-stone-light">
                      <span className="text-stone">Enfants</span>
                      <span className="font-medium text-charcoal">{rsvp?.children?.length || 0}</span>
                    </div>
                  </>
                )}
                {accommodation && accommodation.accommodation_type !== 'none' && (
                  <div className="flex justify-between py-2 border-b border-stone-light">
                    <span className="text-stone flex items-center gap-2">
                      {accommodation.accommodation_type === 'domaine' ? (
                        <Home className="w-4 h-4" />
                      ) : (
                        <Hotel className="w-4 h-4" />
                      )}
                      Hébergement
                    </span>
                    <span className="font-medium text-charcoal">
                      {accommodation.accommodation_type === 'domaine' ? 'Domaine' : 'Hôtel'} - {accommodation.num_rooms} chambre(s)
                    </span>
                  </div>
                )}
                {payment && (
                  <div className="flex justify-between py-2">
                    <span className="text-stone">Paiement</span>
                    <span className={`font-medium ${
                      payment.status === 'paid' ? 'text-sage' :
                      payment.status === 'pending' ? 'text-amber-600' :
                      'text-red-500'
                    }`}>
                      {payment.status === 'paid' ? 'Payé' :
                       payment.status === 'pending' ? 'En attente' :
                       payment.status === 'refunded' ? 'Remboursé' : 'Non payé'} - {payment.amount}€
                    </span>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Event Info */}
            {rsvp?.attending && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-terracotta-light/30 rounded-2xl p-6 mb-8"
              >
                <div className="flex items-center justify-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-terracotta" />
                    <span className="text-charcoal">5 septembre 2026</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-terracotta" />
                    <span className="text-charcoal">Sainte-Eulalie</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row justify-center gap-4"
            >
              <Link to="/programme">
                <Button data-testid="btn-view-programme" className="btn-secondary w-full sm:w-auto">
                  Voir le programme
                </Button>
              </Link>
              <Link to="/">
                <Button data-testid="btn-back-home" className="btn-primary w-full sm:w-auto">
                  Retour à l'accueil
                </Button>
              </Link>
            </motion.div>

            {/* Email Notice */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-8 space-y-3"
            >
              <p className="text-xs text-stone">
                Un email de confirmation a été envoyé à {rsvp?.email}
              </p>
              
              {/* Spam Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                <p className="font-medium mb-1">📧 Vous ne trouvez pas notre email ?</p>
                <p>Pensez à vérifier vos <strong>courriers indésirables (spam)</strong> et ajoutez <strong>mariage@mariage2026-familyperez.fr</strong> à vos contacts.</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default ConfirmationPage;
